import React from 'react';
import SalesDetailPage from './SalesDetailPage';
import { SORT_TYPES } from '../constants/sortTypes';
import {
  buildMemberFilterGroups,
  buildMemberApiFilters,
  memberColumns,
  memberReportBaseConfig,
  renderMemberCell,
} from '../constants/membershipReportShared';
import { currencyFormatter } from '../utils/reportingUtils';

const REVENUE_COLUMNS = memberColumns([
  { id: 'paid_at', label: 'Paid', defaultVisible: true, sortable: true, sortType: 'date' },
  { id: 'payment_type', label: 'Type', defaultVisible: true, sortable: true, sortType: 'text' },
  { id: 'amount_paid', label: 'Amount Paid', defaultVisible: true, sortable: true, sortType: 'currency' },
  { id: 'refunded', label: 'Refunded', defaultVisible: true, sortable: true, sortType: 'currency' },
  { id: 'net_to_venue', label: 'Net to Venue', defaultVisible: true, sortable: true, sortType: 'currency' },
  { id: 'processing_fee', label: 'Processing Fee', defaultVisible: false, sortable: false },
  { id: 'opendate_fee', label: 'Opendate Fee', defaultVisible: false, sortable: false },
], { hidden: ['status', 'amount'] });

const PAYMENT_TYPE_FILTER = {
  id: 'payment_type',
  label: 'Type',
  type: 'checkbox',
  options: [
    { value: 'new', label: 'New' },
    { value: 'renewal', label: 'Renewal' },
  ],
};

const kpiCard = (label, current, prior, key, format) => ({
  label,
  value: format(current[key] || 0),
  current: current[key] || 0,
  prior: prior?.[key],
});

const money = (v) => currencyFormatter.format(v);

const REVENUE_CONFIG = {
  ...memberReportBaseConfig({
    slug: 'revenue',
    title: 'Membership Revenue',
    sidebarKey: 'memberships_revenue',
    localStorageName: 'reportingMembershipsRevenueColumns',
  }),
  pageSubtitle: 'Every membership payment collected in the period, split into new memberships and renewals.',
  showDatePicker: true,
  dateModeOptions: [{ key: 'paid_date', label: 'Paid Date' }],
  detailTableTitle: 'Payments',
  paginationLabel: 'payments',
  itemLabel: (count) => `payment${count !== 1 ? 's' : ''}`,
  searchPlaceholder: 'Search payments...',
  filterTitle: 'Filter Payments',
  filterSubtitle: 'Apply filters to narrow payment data.',
  columns: REVENUE_COLUMNS.columns,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns: REVENUE_COLUMNS.getDefaultVisibleColumns,
  getDefaultHiddenColumns: REVENUE_COLUMNS.getDefaultHiddenColumns,
  defaultSortRules: [{ field: 'paid_at', direction: 'desc' }],
  emptyMessage: 'No membership payments in this period.',

  buildFilterGroups: (venueOwnerships) => buildMemberFilterGroups({
    venueOwnerships, extraFilters: [PAYMENT_TYPE_FILTER],
  }),
  buildDetailApiFilters: buildMemberApiFilters(null, ['payment_type']),

  buildKpiCards: (current, prior) => [
    kpiCard('Total Revenue', current, prior, 'total_revenue', money),
    kpiCard('Net to Venue', current, prior, 'net_to_venue', money),
    kpiCard('New Memberships', current, prior, 'new_revenue', money),
    kpiCard('Renewals', current, prior, 'renewal_revenue', money),
  ],

  salesCardTitle: 'Revenue',
  salesCardIsCurrency: true,
  buildMetricValue: (d) => money(d.total_revenue || 0),
  buildMetricLabel: (d) => `${(d.payments || 0).toLocaleString()} payments`,
  overTimeKey: 'over_time',
  countLabel: 'revenue',
  chartColor: 'var(--sl-fg-brand, #1982C4)',
  topCharts: [
    { title: 'Revenue by Membership', dataKey: 'by_product', barDataKey: 'revenue', nameKey: 'name', barColor: 'var(--sl-fg-brand, #1982C4)', isCurrency: true, tooltipLabel: 'Revenue' },
  ],

  renderCellValue: renderMemberCell(['paid_at']),
};

function ReportingMembershipsRevenuePage(props) {
  return <SalesDetailPage {...props} config={REVENUE_CONFIG} />;
}

export default ReportingMembershipsRevenuePage;
